<template>
    <div class="px-4 py-1 flex items-center flex-col justify-between sm:px-6 fixed bottom-0 left-0 w-full bg-gray-200">
        <nav class="relative z-0 inline-flex shadow-sm">
            <button type="button"
                @click="onClickStart"
                :disabled="isInFirstPage"
                class="relative inline-flex items-center px-3 py-1 border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150"
                :class="{'opacity-50 cursor-not-allowed': isInFirstPage}"
                aria-label="Previous">
                <svg class="h-3 w-3" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path d="M34.5 239L228.9 44.7c9.4-9.4 24.6-9.4 33.9 0l22.7 22.7c9.4 9.4 9.4 24.5 0 33.9L131.5 256l154 154.7c9.3 9.4 9.3 24.5 0 33.9l-22.7 22.7c-9.4 9.4-24.6 9.4-33.9 0L34.5 273c-9.3-9.4-9.3-24.6 0-34zm192 34l194.3 194.3c9.4 9.4 24.6 9.4 33.9 0l22.7-22.7c9.4-9.4 9.4-24.5 0-33.9L323.5 256l154-154.7c9.3-9.4 9.3-24.5 0-33.9l-22.7-22.7c-9.4-9.4-24.6-9.4-33.9 0L226.5 239c-9.3 9.4-9.3 24.6 0 34z"/>
                </svg>
            </button>

            <button type="button"
                @click="onClickPreviousPage"
                :disabled="isInFirstPage"
                class="-ml-px relative inline-flex items-center px-2 py-1 border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150"
                :class="{'opacity-50 cursor-not-allowed': isInFirstPage}"
                aria-label="Previous">
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
            </button>

            <button type="button"
                v-for="(page, index) in pagination.pages"
                :key="index"
                @click="onClickPage(page)"
                :disabled="page === currentPage"
                class="-ml-px relative inline-flex items-center px-3 py-1 border border-gray-400 bg-white text-xs leading-5 font-medium text-gray-700 hover:bg-gray-200 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                :class="{'bg-gray-200 cursor-default': currentPage === page}">
                {{ page }}
            </button>

            <button type="button"
                @click="onClickNextPage"
                :disabled="isInLastPage"
                class="-ml-px relative inline-flex items-center px-2 py-1 border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150"
                :class="{'opacity-50 cursor-not-allowed': isInLastPage}"
                aria-label="Next">
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                </svg>
            </button>

            <button type="button"
                @click="onClickLast"
                :disabled="isInLastPage"
                class="-ml-px relative inline-flex items-center px-3 py-1 border border-gray-400 bg-white text-sm leading-5 font-medium text-gray-500 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150"
                :class="{'opacity-50 cursor-not-allowed': isInLastPage}"
                aria-label="Previous">
                <svg class="h-3 w-3" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path d="M477.5 273L283.1 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.7-22.7c-9.4-9.4-9.4-24.5 0-33.9l154-154.7-154-154.7c-9.3-9.4-9.3-24.5 0-33.9l22.7-22.7c9.4-9.4 24.6-9.4 33.9 0L477.5 239c9.3 9.4 9.3 24.6 0 34zm-192-34L91.1 44.7c-9.4-9.4-24.6-9.4-33.9 0L34.5 67.4c-9.4 9.4-9.4 24.5 0 33.9l154 154.7-154 154.7c-9.3 9.4-9.3 24.5 0 33.9l22.7 22.7c9.4 9.4 24.6 9.4 33.9 0L285.5 273c9.3-9.4 9.3-24.6 0-34z"/>
                </svg>
            </button>
        </nav>
        <div>
            <p class="text-1xs leading-5 text-gray-700">
                Showing
                <span class="font-medium">{{ from }}</span>
                to
                <span class="font-medium">{{ to }}</span>
                of
                <span class="font-medium">{{ total }}</span>
                results
            </p>
        </div>
    </div>
</template>
<script>
    export default {
        props: {
            maxVisibleButtons: {
                type: Number,
                required: false,
                default: 3
            },
            perPage: {
                type: Number,
                required: true
            },
            total: {
                type: Number,
                required: true
            },
            currentPage: {
                type: Number,
                required: true
            }
        },
        methods: {
            onClickPreviousPage() {
                this.$emit('paginate', this.currentPage - 1);
            },
            onClickPage(page) {
                this.$emit('paginate', page);
            },
            onClickNextPage() {
                this.$emit('paginate', this.currentPage + 1);
            },
            onClickStart() {
                this.$emit('paginate', 1);
            },
            onClickLast() {
                this.$emit('paginate', this.totalPages);
            }
        },
        computed: {
            isInFirstPage() {
                return this.currentPage === 1;
            },
            isInLastPage() {
                return this.currentPage === this.totalPages;
            },
            startPage() {
                // When on the first page
                if (this.currentPage === 1) {
                    return 1;
                }
                // When on the last page
                if (this.currentPage === this.totalPages) {
                    return this.totalPages - this.maxVisibleButtons + 1;
                }
                // When in between
                let value = Math.floor((this.maxVisibleButtons - 1) / 2) - this.maxVisibleButtons;

                if (value < 1){
                    return 1;
                } else {
                    return value;
                }
            },
            totalPages() {
                return Math.ceil((this.total / this.perPage));
            },
            pagination() {
                const totalItems = this.total;
                let currentPage = this.currentPage;
                const pageSize = this.perPage;
                const maxPages = this.maxVisibleButtons;

                // calculate total pages
                let totalPages = Math.ceil(totalItems / pageSize);

                // ensure current page isn't out of range
                if (currentPage < 1) {
                    currentPage = 1;
                } else if (currentPage > totalPages) {
                    currentPage = totalPages;
                }

                let startPage, endPage;

                if (totalPages <= maxPages) {
                    // total pages less than max so show all pages
                    startPage = 1;
                    endPage = totalPages;
                } else {
                    // total pages more than max so calculate start and end pages
                    let maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
                    let maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;
                    if (currentPage <= maxPagesBeforeCurrentPage) {
                        // current page near the start
                        startPage = 1;
                        endPage = maxPages;
                    } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
                        // current page near the end
                        startPage = totalPages - maxPages + 1;
                        endPage = totalPages;
                    } else {
                        // current page somewhere in the middle
                        startPage = currentPage - maxPagesBeforeCurrentPage;
                        endPage = currentPage + maxPagesAfterCurrentPage;
                    }
                }

                // calculate start and end item indexes
                let startIndex = (currentPage - 1) * pageSize;
                let endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

                // create an array of pages to ng-repeat in the pager control
                let pages = Array.from(Array((endPage + 1) - startPage).keys()).map(i => startPage + i);

                // return object with all pager properties required by the view
                return {
                    totalItems: totalItems,
                    currentPage: currentPage,
                    pageSize: pageSize,
                    totalPages: totalPages,
                    startPage: startPage,
                    endPage: endPage,
                    startIndex: startIndex,
                    endIndex: endIndex,
                    pages: pages
                };
            },
            from(){
                if (this.currentPage === 1) {
                    return 1;
                }

                return ((this.perPage * this.currentPage) - this.perPage) + 1;
            },
            to(){
                if (this.total < (this.perPage * this.currentPage)){
                    return this.total;
                } else {
                    return (this.perPage * this.currentPage);
                }
            }
        }
    };
</script>
